import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  Autocomplete,
  Skeleton,
  Chip,
  IconButton,
  Avatar,
} from "@mui/material";
import { HiPencil, HiPhotograph, HiTrash } from "react-icons/hi";
import { useSnackbar } from "notistack";
import { useAdminUpdateOrganization } from "@/api/hooks/admin.hooks";
import { useOrganizations } from "@/api/hooks/organization.hooks";
import { useDynamicPropertyTypes } from "@/api/hooks/useDynamicPropertyTypes";
import {
  useOrganizationDynamicProperties,
  useUpdateOrganizationDynamicProperty,
  useCreateOrganizationDynamicProperty,
} from "@/api/hooks/useOrganizationDynamicProperties";
import { getOrgInitials } from "@/utils/getOrgInitials";
import type { OrganizationDto } from "@/api/types/dtos";
import type { DynamicPropertyType } from "@/api/hooks/useDynamicPropertyTypes";

interface DynamicPropertyValue {
  id?: string;
  propertyTypeId: string;
  value: string;
  displayName: string;
  hasChanged: boolean;
}

interface EditOrganizationModalProps {
  open: boolean;
  organization: OrganizationDto | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  open,
  organization,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const updateMutation = useAdminUpdateOrganization();
  const updateDynamicPropertyMutation = useUpdateOrganizationDynamicProperty();
  const createDynamicPropertyMutation = useCreateOrganizationDynamicProperty();

  const [name, setName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [parentOrganizationId, setParentOrganizationId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dynamicPropertyValues, setDynamicPropertyValues] = useState<DynamicPropertyValue[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: parentOrgsData } = useOrganizations();
  const parentOrgs = (parentOrgsData?.data ?? []) as OrganizationDto[];

  const { data: dynamicPropertyTypesData, isLoading: isLoadingPropTypes } =
    useDynamicPropertyTypes();
  const { data: orgDynamicPropsData, isLoading: isLoadingOrgProps } =
    useOrganizationDynamicProperties(organization?.id || "");

  const propertyTypes: DynamicPropertyType[] = useMemo(
    () => dynamicPropertyTypesData?.data ?? [],
    [dynamicPropertyTypesData]
  );
  const orgDynamicProps = useMemo(() => orgDynamicPropsData?.data ?? [], [orgDynamicPropsData]);

  const isMainOrganization = !organization?.parentOrganizationId;

  useEffect(() => {
    if (organization) {
      setName(organization.name || "");
      setIdNumber(organization.idNumber || "");
      setParentOrganizationId(organization.parentOrganizationId || null);
      setIsActive(organization.isActive);
      setErrors({});
      setLogoFile(null);
      setLogoPreview(organization.logo || null);
    }
  }, [organization]);

  useEffect(() => {
    if (!isMainOrganization || propertyTypes.length === 0) return;

    const mapped: DynamicPropertyValue[] = propertyTypes.map((pt) => {
      const existing = orgDynamicProps.find((p) => p.propertyTypeId === pt.id);
      return {
        id: existing?.id,
        propertyTypeId: pt.id,
        value: existing?.value || pt.defaultValue || "",
        displayName: pt.displayName || pt.name,
        hasChanged: false,
      };
    });
    setDynamicPropertyValues(mapped);
  }, [isMainOrganization, propertyTypes, orgDynamicProps]);

  const handleDynamicPropertyChange = useCallback((propertyTypeId: string, newValue: string) => {
    setDynamicPropertyValues((prev) =>
      prev.map((p) =>
        p.propertyTypeId === propertyTypeId ? { ...p, value: newValue, hasChanged: true } : p
      )
    );
  }, []);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Solo se permiten archivos de imagen", { variant: "error" });
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    if (name.trim() && name.trim().length < 2) {
      newErrors.name = "El nombre debe tener al menos 2 caracteres";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!organization || !validate()) return;

    try {
      await new Promise<void>((resolve, reject) => {
        updateMutation.mutate(
          {
            id: organization.id,
            data: {
              name: name.trim(),
              idNumber: idNumber.trim(),
              parentOrganizationId: parentOrganizationId || undefined,
              isActive,
              logo: logoFile ? undefined : logoPreview,
            },
            logoFile,
          },
          {
            onSuccess: () => resolve(),
            onError: (error: unknown) => {
              const axiosError = error as {
                response?: { data?: { description?: string; message?: string } };
              };
              const message =
                axiosError?.response?.data?.description ||
                axiosError?.response?.data?.message ||
                "Error al actualizar la organización";
              reject(new Error(message));
            },
          }
        );
      });

      if (isMainOrganization) {
        const changedProps = dynamicPropertyValues.filter((p) => p.hasChanged);
        for (const prop of changedProps) {
          try {
            if (prop.id) {
              await new Promise<void>((resolve, reject) => {
                updateDynamicPropertyMutation.mutate(
                  { id: prop.id || '', data: { value: prop.value || '' } },
                  {
                    onSuccess: () => resolve(),
                    onError: () => reject(new Error(`Error al actualizar ${prop.displayName}`)),
                  }
                );
              });
            } else {
              await new Promise<void>((resolve, reject) => {
                createDynamicPropertyMutation.mutate(
                  {
                    organizationId: organization.id,
                    data: {
                      propertyTypeId: prop.propertyTypeId,
                      value: prop.value,
                    },
                  },
                  {
                    onSuccess: () => resolve(),
                    onError: () => reject(new Error(`Error al crear ${prop.displayName}`)),
                  }
                );
              });
            }
          } catch {
            // Continue with other properties on error
          }
        }
      }

      enqueueSnackbar("Organización actualizada exitosamente", { variant: "success" });
      onSuccess();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Error al actualizar la organización";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const isSubmitting =
    updateMutation.isPending ||
    updateDynamicPropertyMutation.isPending ||
    createDynamicPropertyMutation.isPending;

  const handleClose = () => {
    if (isSubmitting) return;
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-org-dialog-title"
      PaperProps={{
        sx: { borderTop: "4px solid", borderColor: "primary.main" },
      }}
    >
      <DialogTitle id="edit-org-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <HiPencil size={22} />
          <Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              Editar Organización
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {organization?.name || ""}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Información General
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nombre de la Organización"
                required
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                disabled={isSubmitting}
              />
              <TextField
                label="Número de Documento / Identificación"
                fullWidth
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                disabled={isSubmitting}
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Logo
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {logoPreview ? (
                <Avatar
                  src={logoPreview}
                  sx={{ width: 64, height: 64 }}
                  variant="rounded"
                />
              ) : (
                <Avatar
                  sx={{ width: 64, height: 64, bgcolor: "primary.main" }}
                  variant="rounded"
                >
                  <Typography variant="h6">
                    {getOrgInitials(name || organization?.name || "")}
                  </Typography>
                </Avatar>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                style={{ display: "none" }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HiPhotograph size={16} />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  {logoPreview ? "Cambiar" : "Subir"}
                </Button>
                {logoPreview && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleLogoRemove}
                    disabled={isSubmitting}
                  >
                    <HiTrash size={18} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Relación Jerárquica
            </Typography>
            <Autocomplete
              options={parentOrgs.filter((o) => o.id !== organization?.id)}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={parentOrgs.find((o) => o.id === parentOrganizationId) || null}
              onChange={(_, value) => setParentOrganizationId(value?.id || null)}
              disabled={isSubmitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organización Padre"
                  placeholder="Buscar organización..."
                  helperText="Déjalo vacío si es una organización principal"
                />
              )}
              renderOption={(props: any, option: OrganizationDto) => {
                const { key: _key, ...rest } = props;
                return (
                  <li key={option.id} {...rest}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{option.name}</span>
                      {!option.isActive && <Chip label="Inactiva" size="small" color="default" />}
                    </Box>
                  </li>
                );
              }}
            />
          </Box>

          <Divider />

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Estado
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={isSubmitting}
                    color="success"
                  />
                }
                label={
                  <Chip
                    label={isActive ? "Activa" : "Inactiva"}
                    color={isActive ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                }
                sx={{ mr: 0 }}
              />
            </Box>
          </Box>

          {isMainOrganization && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Propiedades Dinámicas
                </Typography>
                {isLoadingPropTypes || isLoadingOrgProps ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} height={48} />
                    ))}
                  </Box>
                ) : dynamicPropertyValues.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {dynamicPropertyValues.map((prop) => {
                      const propType = propertyTypes.find((pt) => pt.id === prop.propertyTypeId);
                      const isBoolean = propType?.dataType?.name === "boolean";

                      return (
                        <Box key={prop.propertyTypeId}>
                          {isBoolean ? (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={prop.value === "1"}
                                  onChange={(e) =>
                                    handleDynamicPropertyChange(
                                      prop.propertyTypeId,
                                      e.target.checked ? "1" : "0"
                                    )
                                  }
                                  color="success"
                                />
                              }
                              label={<Typography variant="body1">{prop.displayName}</Typography>}
                            />
                          ) : (
                            <TextField
                              label={prop.displayName}
                              fullWidth
                              value={prop.value}
                              onChange={(e) =>
                                handleDynamicPropertyChange(prop.propertyTypeId, e.target.value)
                              }
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay propiedades dinámicas configuradas para esta organización.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting} sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : <HiPencil size={16} />
          }
          sx={{ minWidth: 140 }}
        >
          {isSubmitting ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrganizationModal;
